import { ILoginDto } from "@kuika/kuika-cl-model";
import Form, { FormInstance, useForm } from "rc-field-form";
import * as React from "react";
import { KNavigator } from "../../shared/hoc/with-history";
import KuikaButton from "../ui-kit/kuika-button/kuika-button";
import { KuikaFormField } from "../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../ui-kit/kuika-svg/kuika-svg";
import { toastService } from "../ui-kit/kuika-toast/toast-service";
import { GoogleLogin } from "./google-login";
import { SignInDivider, SignInPasswordField } from "./sign-in-fields";
import { goToForgotPassword, IPasswordSignIn, usePasswordSignIn } from "./use-password-sign-in";

export interface ISignInFormProps {
  isLoginHTML: boolean;
  navigator: KNavigator;
  isGoogleAvailable: boolean;
  /** Present when this device remembers accounts, so "Use another account" can be undone. */
  onBackToSavedAccounts?: () => void;
  /** Focus the email field on arrival — set when the person navigated here, not on page load. */
  autoFocus?: boolean;
}

/** The plain sign-in: Google, then email and password. What a first visit, or a new person, sees. */
export const SignInForm = ({
  isLoginHTML,
  navigator,
  isGoogleAvailable,
  onBackToSavedAccounts,
  autoFocus
}: ISignInFormProps): React.ReactElement => {
  const [form]: [FormInstance<ILoginDto>] = useForm<ILoginDto>();
  const signIn: IPasswordSignIn = usePasswordSignIn({ form, isLoginHTML, navigator });
  const [isGoogleLoading, setIsGoogleLoading] = React.useState<boolean>(false);
  // While an attempt is on the wire, leaving this view would unmount the form mid-request: the
  // answer would then sign in (and remember) an account the person has already moved away from.
  const isBusy: boolean = signIn.isSubmitting || isGoogleLoading;

  const onGoogleSuccess: () => void = React.useCallback((): void => {
    form.resetFields();
    setIsGoogleLoading(true);
  }, [form]);

  const onGoogleFailure: (error?: string) => void = React.useCallback((error?: string): void => {
    setIsGoogleLoading(false);
    toastService.error(error || "Something went wrong.");
  }, []);

  const onForgotPassword: () => void = React.useCallback((): void => {
    goToForgotPassword(isLoginHTML, navigator);
  }, [isLoginHTML, navigator]);

  return (
    <div className="auth-page__panel" data-testid="signInForm">
      <h1 className="auth-page__title">Sign in to your account</h1>

      {isGoogleAvailable && (
        <>
          <GoogleLogin
            loading={isGoogleLoading}
            routing={isLoginHTML ? "static" : "dynamic"}
            type="login"
            onSuccess={onGoogleSuccess}
            onFailure={onGoogleFailure}
          />
          <SignInDivider />
        </>
      )}

      <Form
        form={form}
        onFinish={signIn.submit}
        onValuesChange={signIn.clearCredentialsError}
        className="auth-page__credentials"
      >
        <KuikaFormField
          name="username"
          label="Email"
          requiredMarker={false}
          errorTestId="signInEmailError"
          rules={[{ required: true, message: "Please enter your email" }]}
        >
          <KuikaInput
            size="lg"
            autoFocus={autoFocus}
            leftIcon={<KuikaSvg name="mail" />}
            placeholder="name@company.com"
            autoComplete="username"
            inputMode="email"
            autoCapitalize="none"
            spellCheck={false}
            disabled={isGoogleLoading}
            data-testid="signInEmailInput"
          />
        </KuikaFormField>

        <SignInPasswordField disabled={isGoogleLoading} onForgotPassword={onForgotPassword} />

        {signIn.emailToResend !== undefined && (
          <KuikaButton
            type="text"
            htmlType="button"
            onClick={signIn.resendVerificationEmail}
            loading={signIn.isResending}
            data-testid="resendVerificationButton"
          >
            Resend verification email
          </KuikaButton>
        )}

        <KuikaButton
          data-testid="signInButton"
          htmlType="submit"
          size="lg"
          loading={signIn.isSubmitting}
          disabled={isGoogleLoading}
          className="kuika-width-full"
        >
          Sign in
        </KuikaButton>
      </Form>

      {onBackToSavedAccounts && (
        <KuikaButton
          type="text"
          htmlType="button"
          onClick={onBackToSavedAccounts}
          disabled={isBusy}
          className="auth-page__back-link"
          data-testid="backToSavedAccountsButton"
        >
          Back to saved accounts
        </KuikaButton>
      )}
    </div>
  );
};
